import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { queries, type ComuneLight } from '@/data';
import { useSession } from '@/lib/auth-client';
import { useUnitTerms } from '@/features/map-core/useUnitTerms';
import { emit } from '@/lib/events';
import { useShortlistStore, useActiveShortlist, DIST_PREFIX } from './store';
import { exportShortlistCsv } from './export';
import { useVisitPlannerStore } from '@/features/visit-planner';
import { checklistPath } from '@/features/next-steps/slugs';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function ShortlistDrawer({ open, onClose }: Props) {
  const { t } = useTranslation();
  const terms = useUnitTerms();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const lists = useShortlistStore((s) => s.lists);
  const activeId = useShortlistStore((s) => s.activeId);
  const setActive = useShortlistStore((s) => s.setActive);
  const createLocal = useShortlistStore((s) => s.createLocal);
  const rename = useShortlistStore((s) => s.rename);
  const remove = useShortlistStore((s) => s.remove);
  const toggleComune = useShortlistStore((s) => s.toggleComune);
  const setNote = useShortlistStore((s) => s.setNote);

  const active = useActiveShortlist();
  const [newName, setNewName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const { data: meta } = useQuery(queries.meta());
  const { data: light } = useQuery({
    ...queries.light(meta?.version ?? ''),
    enabled: !!meta?.version,
  });
  const { data: distMeta } = useQuery({
    ...queries.districtsMeta(meta?.version ?? ''),
    enabled: !!meta?.version,
  });

  const byIstat = useMemo(() => {
    const map = new Map<string, ComuneLight>();
    if (light) for (const c of light) map.set(c.istat, c);
    return map;
  }, [light]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isGuest = !session;
  const dirtyCount = lists.filter((l) => l.dirty).length;

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) return;
    createLocal(name, 'IT');
    setNewName('');
  };

  // Comune-only действия (compare / visit-planner) игнорируют сохранённые
  // районы — они работают по конкретным istat коммун.
  const comuneOnlyIstats = active
    ? active.comuneIstats.filter((i) => !i.startsWith(DIST_PREFIX))
    : [];

  const handleCompare = () => {
    if (comuneOnlyIstats.length < 2) return;
    navigate(`/it/compare?ids=${comuneOnlyIstats.join(',')}`);
    onClose();
  };

  const handleShowOnMap = () => {
    if (!active || !light) return;
    const items = active.comuneIstats.map((i) => byIstat.get(i)).filter(Boolean) as ComuneLight[];
    if (items.length === 0) return;
    emit('places:flyToBounds', { points: items.map((c) => ({ lat: c.lat, lon: c.lon })) });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[1100]" role="dialog" aria-modal="true" aria-labelledby="shortlist-title">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/30"
        onClick={onClose}
        aria-label={t('shortlist.close')}
        tabIndex={-1}
      />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 id="shortlist-title" className="text-base font-semibold text-slate-800">
            {t('shortlist.title')}
          </h2>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {dirtyCount > 0 && session && <span aria-live="polite">{t('shortlist.syncingDirty')}</span>}
            <button
              type="button"
              onClick={onClose}
              aria-label={t('shortlist.close')}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              ✕
            </button>
          </div>
        </header>

        {isGuest && (
          <div className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-900">
            {t('shortlist.guestHint')}{' '}
            <button onClick={() => navigate('/login')} className="font-semibold underline">
              {t('shortlist.loginToSync')}
            </button>
          </div>
        )}

        {/* Lists picker */}
        <div className="border-b border-slate-200 px-4 py-3">
          <div className="mb-2 flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t('shortlist.createPlaceholder')}
              className="flex-1 rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <button
              type="button"
              onClick={handleCreate}
              disabled={!newName.trim()}
              className="rounded-md bg-[color:var(--color-terra-600)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-paper)] hover:bg-[color:var(--color-terra-700)] disabled:opacity-50"
            >
              {t('shortlist.createSubmit')}
            </button>
          </div>

          {lists.length === 0 ? (
            <p className="py-2 text-xs text-slate-500">{t('shortlist.emptyHint', terms)}</p>
          ) : (
            <ul className="space-y-1">
              {lists.map((l) => {
                const isActive = l.id === activeId;
                const isRenaming = renamingId === l.id;
                return (
                  <li
                    key={l.id}
                    className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
                      isActive ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-slate-50'
                    }`}
                  >
                    {isRenaming ? (
                      <input
                        // eslint-disable-next-line jsx-a11y/no-autofocus -- focus trap for accessibility: inline rename input should receive focus immediately
                        autoFocus
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => {
                          if (renameValue.trim()) rename(l.id, renameValue);
                          setRenamingId(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (renameValue.trim()) rename(l.id, renameValue);
                            setRenamingId(null);
                          } else if (e.key === 'Escape') {
                            setRenamingId(null);
                          }
                        }}
                        className="flex-1 rounded border border-slate-300 px-1.5 py-0.5 text-sm"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setActive(l.id)}
                        className="flex-1 truncate text-left text-slate-800"
                      >
                        {l.name}
                        <span className="ml-2 text-[11px] text-slate-400">
                          {t('shortlist.items', { count: l.comuneIstats.length, ...terms })}
                        </span>
                      </button>
                    )}
                    {isActive && !isRenaming && (
                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                        {t('shortlist.activeBadge')}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setRenamingId(l.id);
                        setRenameValue(l.name);
                      }}
                      className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                      title={t('shortlist.rename')}
                      aria-label={t('shortlist.rename')}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(t('shortlist.removeConfirm'))) remove(l.id);
                      }}
                      className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      title={t('shortlist.remove')}
                      aria-label={t('shortlist.remove')}
                    >
                      🗑
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Active list contents */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {!active ? (
            <div className="flex flex-1 items-center justify-center px-4 py-8 text-center text-sm text-slate-500">
              {t('shortlist.empty')}
            </div>
          ) : active.comuneIstats.length === 0 ? (
            <div className="flex flex-1 items-center justify-center px-4 py-8 text-center text-sm text-slate-500">
              {t('shortlist.emptyHint', terms)}
            </div>
          ) : (
            <ul className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {active.comuneIstats.map((entry) => {
                // Район (namespaced `dist:<code>`) — сохранён как одна сущность.
                if (entry.startsWith(DIST_PREFIX)) {
                  const code = entry.slice(DIST_PREFIX.length);
                  const info = distMeta?.get(code);
                  const dname = info?.name_alt ? `${info.name} / ${info.name_alt}` : info?.name ?? code;
                  const kindLabel =
                    t(`district.kind.${info?.kind || 'sll'}`);
                  const note = active.notes[entry] ?? '';
                  return (
                    <ShortlistItem
                      key={entry}
                      name={`🏞 ${dname}`}
                      prov={info ? `${kindLabel} · ${info.n_units}` : kindLabel}
                      note={note}
                      onFlyTo={() => undefined}
                      onRemove={() => toggleComune(active.id, entry)}
                      onNoteChange={(v) => setNote(active.id, entry, v)}
                      t={t}
                    />
                  );
                }
                const c = byIstat.get(entry);
                if (!c) return null;
                const note = active.notes[entry] ?? '';
                return (
                  <ShortlistItem
                    key={entry}
                    name={c.name}
                    prov={c.prov}
                    note={note}
                    onFlyTo={() => emit('places:flyToPoint', { lat: c.lat, lon: c.lon, zoom: 12 })}
                    onRemove={() => toggleComune(active.id, entry)}
                    onNoteChange={(v) => setNote(active.id, entry, v)}
                    t={t}
                  />
                );
              })}
            </ul>
          )}

          {/* Bottom action bar. Buttons share an icon-on-top layout so они
              получаются одинакового размера независимо от длины подписи. */}
          {active && active.comuneIstats.length > 0 && (
            <div className="grid shrink-0 grid-cols-5 gap-2 border-t border-slate-200 bg-slate-50 px-3 py-2 text-[11px]">
              <button
                type="button"
                onClick={handleShowOnMap}
                className="flex h-14 flex-col items-center justify-center gap-1 rounded-md border border-slate-300 bg-white px-1 text-center leading-tight hover:bg-slate-50"
              >
                <span aria-hidden="true" className="text-base leading-none">📍</span>
                <span className="line-clamp-2">{t('shortlist.viewOnMap')}</span>
              </button>
              <button
                type="button"
                onClick={handleCompare}
                disabled={comuneOnlyIstats.length < 2}
                className="flex h-14 flex-col items-center justify-center gap-1 rounded-md border border-slate-300 bg-white px-1 text-center leading-tight hover:bg-slate-50 disabled:opacity-50"
              >
                <span aria-hidden="true" className="text-base leading-none">⚖</span>
                <span className="line-clamp-2">{t('shortlist.compare')}</span>
              </button>
              <button
                type="button"
                onClick={() => exportShortlistCsv(active, byIstat)}
                className="flex h-14 flex-col items-center justify-center gap-1 rounded-md border border-slate-300 bg-white px-1 text-center leading-tight hover:bg-slate-50"
              >
                <span aria-hidden="true" className="text-base leading-none">⬇</span>
                <span className="line-clamp-2">{t('shortlist.exportCsv')}</span>
              </button>
              <button
                type="button"
                onClick={() => useVisitPlannerStore.getState().open(comuneOnlyIstats.slice(0, 7))}
                disabled={comuneOnlyIstats.length < 2}
                title={
                  comuneOnlyIstats.length > 7
                    ? t('shortlist.planVisitTrim', {
                        defaultValue: 'Routes only first 7 of {{count}} stops.',
                        count: comuneOnlyIstats.length,
                      })
                    : t('shortlist.planVisit', { defaultValue: 'Plan a driving visit' })
                }
                className="flex h-14 flex-col items-center justify-center gap-1 rounded-md border border-slate-300 bg-white px-1 text-center leading-tight hover:bg-slate-50 disabled:opacity-50"
              >
                <span aria-hidden="true" className="text-base leading-none">🗺</span>
                <span className="line-clamp-2">{t('shortlist.planVisit', { defaultValue: 'Plan visit' })}</span>
              </button>
              {/* D12 — once the user has narrowed down to a shortlist, surface
                  the post-decision checklist. The map only ships Italy data
                  today, so we hard-code IT; broaden when more countries land. */}
              <Link
                to={checklistPath('IT')}
                className="flex h-14 flex-col items-center justify-center gap-1 rounded-md border border-[color:var(--color-moss-500)] bg-[color:var(--color-moss-50)] px-1 text-center font-semibold leading-tight text-[color:var(--color-moss-700)] hover:bg-[color:var(--color-moss-100)]"
                title={t('shortlist.checklistHint', {
                  defaultValue: 'Next steps after deciding: visa, codice fiscale, residency',
                })}
              >
                <span aria-hidden="true" className="text-base leading-none">✓</span>
                <span className="line-clamp-2">{t('shortlist.checklist', { defaultValue: 'Moving checklist' })}</span>
              </Link>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

type ItemProps = {
  name: string;
  prov: string;
  note: string;
  onFlyTo: () => void;
  onRemove: () => void;
  onNoteChange: (v: string) => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
};

function ShortlistItem({ name, prov, note, onFlyTo, onRemove, onNoteChange, t }: ItemProps) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(note);

  return (
    <li className="px-3 py-2">
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onFlyTo}
          className="flex-1 text-left"
        >
          <div className="text-sm font-medium text-slate-800">{name}</div>
          <div className="text-[11px] text-slate-400">{prov}</div>
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-600"
          aria-label={t('shortlist.removeFromList')}
          title={t('shortlist.removeFromList')}
        >
          ✕
        </button>
      </div>
      {editing ? (
        <div className="mt-1.5 flex gap-1">
          <input
            // eslint-disable-next-line jsx-a11y/no-autofocus -- focus trap for accessibility: inline note input should receive focus immediately
            autoFocus
            type="text"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder={t('shortlist.notePlaceholder')}
            className="flex-1 rounded border border-slate-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none"
            onBlur={() => {
              onNoteChange(val);
              setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onNoteChange(val);
                setEditing(false);
              } else if (e.key === 'Escape') {
                setVal(note);
                setEditing(false);
              }
            }}
          />
        </div>
      ) : note ? (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mt-1 block w-full rounded border border-transparent bg-slate-50 px-2 py-1 text-left text-xs italic text-slate-600 hover:border-slate-200"
        >
          “{note}”
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mt-1 text-[11px] text-slate-400 hover:text-slate-600"
        >
          + {t('shortlist.notePlaceholder')}
        </button>
      )}
    </li>
  );
}
